import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { Controller, useForm } from 'react-hook-form'
import LayoutSidebar from '../../../components/LayoutSidebar'
import { selectAuth } from '../../../redux/authSlice'

export default function PaymentProcessorSplit() {
  const requesting = useRef(false)
  const request = useRequest()
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const { register, handleSubmit, control, watch, reset } = useForm();
  const [responseData, setResponseData] = useState([])
  const [totals, setTotals] = useState([]); // Add this to keep track of ACH totals
  const [debitCardTotals, setDebitCardTotals] = useState([]); // Add this to keep track of Debit Card totals
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken) {
      getPaymentProcessorSplit()
    }
  }, [auth.accessToken])

  const getPaymentProcessorSplit = async () => {
    if (requesting.current) return;
    requesting.current = true;
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/payment_processor_split_info`)
      const response = await request.get()
      setResponseData(response?.data?.data);
      requesting.current = false;
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      requesting.current = false;
    }
  }

  useEffect(() => {
    computeTotals();
    computeDebitCardTotals();
  }, [responseData])

  const computeTotals = () => {
    const newTotals = responseData?.portfolios?.map((portfolio) => {
      return responseData?.processors?.reduce((sum, processor) => {
        if (!processor.is_debit_card) {
          const split = responseData?.splits?.find(
            (split) => split.processor_company_dropdown_option_id === processor.id && split.portfolio_id === portfolio.id
          );
          const splitPercentage = split ? split.split_percentage : 0;
          return sum + parseFloat(splitPercentage);
        }

        return sum;
      }, 0);
    });
    setTotals(newTotals);
  }

  const computeDebitCardTotals = () => {
    const newTotals = responseData?.portfolios?.map((portfolio) => {
      return responseData?.processors?.reduce((sum, processor) => {
        if (processor.is_debit_card) {
          const split = responseData?.splits?.find(
            (split) => split.processor_company_dropdown_option_id === processor.id && split.portfolio_id === portfolio.id
          );
          const splitPercentage = split ? split.split_percentage : 0;
          return sum + parseFloat(splitPercentage);
        }

        return sum;
      }, 0);
    });
    setDebitCardTotals(newTotals);
  }

  const onSubmit = async (formData) => {
    setSaving(true)
    if (totals.some((value) => value !== 100)) {
      setSaving(false)
      dispatch(setNotification({ type: 'error', body: 'Sum of split percentage should be 100 for each portfolio.' }))
      return;
    }
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/payment_processor_split`)
    try {
      // Remove null values from the form data arrays
      formData['portfolios'] = formData['portfolios'].filter((portfolio) => portfolio !== null);
      formData['processors'] = formData['processors'].filter((processor) => processor !== null);

      const response = await request.post(formData)
      if (response.status === 200 || response.status === 201 || response.status === 204) {
        setSaving(false)
        dispatch(setNotification({ type: 'success', body: '' }))
        getPaymentProcessorSplit()
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  };

  useEffect(() => {
    const unsub = watch((values) => {
      setPortfolioSplitSum(values);
      setDebitCardPortfolioSplitSum(values);
    });
  }, [watch, responseData]);

  // Update the totals whenever any input field value changes
  const setPortfolioSplitSum = (values) => { // Add the event parameter
    const newTotals = responseData?.portfolios?.map((portfolio) => {
      return responseData?.processors?.reduce((sum, processor) => {
        if (!processor.is_debit_card) {
          const value = parseFloat(values?.split_percentages[portfolio.id][processor.id] || 0); // Use getValues to fetch the value
          return sum + value;
        }

        return sum;
      }, 0);
    });

    setTotals(newTotals);
  };

  // Update the totals whenever any input field value changes
  const setDebitCardPortfolioSplitSum = (values) => { // Add the event parameter
    const newTotals = responseData?.portfolios?.map((portfolio) => {
      return responseData?.processors?.reduce((sum, processor) => {
        if (processor.is_debit_card) {
          const value = parseFloat(values?.split_percentages[portfolio.id][processor.id] || 0); // Use getValues to fetch the value
          return sum + value;
        }

        return sum;
      }, 0);
    });

    setDebitCardTotals(newTotals);
  };

  const handleCancel = () => {
    const splitPercentages = {}

    responseData.portfolios?.forEach((portfolio) => {
      splitPercentages[portfolio.id] = responseData.processors?.reduce((values, processor) => {
        const split = responseData.splits?.find(
          (split) => split.processor_company_dropdown_option_id === processor.id && split.portfolio_id === portfolio.id
        );
        const splitPercentage = split ? split.split_percentage : 0;

        return {
          ...values,
          [processor.id]: splitPercentage
        };
      }, {});
    })

    const defaultValues = {
      split_percentages: splitPercentages
    };
    reset(defaultValues);
  }

  return (
    <LayoutSidebar
      contentTitle={'Payment Processor Split'}
      content={(
        <div className="lox-detail mt-1 mx-1">
          <form onSubmit={handleSubmit(onSubmit)} id="payment_processor_split_form">
            <div className="columns mb-0">
              <div
                className="column is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start"
              >
                <div className="title is-4 has-text-primary is-flex">ACH Payment Processor Company % Split</div>
                <p className="subtitle is-6">INSTRUCTIONS: Fill in the split % for each ACH processor company for each
                  portfolio. Each portfolio&apos;s split % must equal 100%.</p>
              </div>
            </div>

            {responseData.length !== 0 && (
              <div className="table-container mb-0">
                <table
                  className="lox-hooktable-table table is-bordered is-striped is-narrow is-fullwidth is-hoverable mb-3"
                >
                  <thead>
                  <tr>
                    <th className="is-selected has-text-centered"></th>
                    {responseData?.portfolios?.map((portfolio) => (
                      <th className="is-selected has-text-centered" key={portfolio.id}>{portfolio.name}</th>
                    ))}
                  </tr>
                  </thead>
                  <tbody>
                  {responseData?.processors?.map((processor) => {
                    if (!processor.is_debit_card) {
                      return (
                        <tr key={processor.id}>
                          <td className="has-text-centered">{processor.name}</td>
                          {responseData?.portfolios?.map((portfolio) => {
                            // Find the corresponding split object
                            const split = responseData?.splits?.find(
                              (split) => split.processor_company_dropdown_option_id === processor.id && split.portfolio_id === portfolio.id,
                            )

                            // Extract the split_percentage value or set it to empty string if no split object is found
                            const splitPercentage = split ? split.split_percentage : 0
                            const inputName = `[${portfolio.id}][${processor.id}]`

                            return (
                              <td className="has-text-centered" key={portfolio.id}>
                                <Controller
                                  name={`split_percentages${inputName}`}
                                  control={control}
                                  defaultValue={splitPercentage}
                                  render={({ field: { onChange } }) => (
                                    <input
                                      {...register(`split_percentages${inputName}`)}
                                      name={`split_percentages${inputName}`}
                                      className="input is-small"
                                      type="number"
                                      form="payment_processor_split_form"
                                      min={0}
                                      max={100}
                                      step="1"
                                    />
                                  )}
                                />
                                <input
                                  type="hidden"
                                  name={`processors[${processor.id}]`}
                                  value={processor.id}
                                  {...register(`processors[${processor.id}]`)}
                                />
                                <input
                                  type="hidden"
                                  name={`portfolios[${portfolio.id}]`}
                                  value={portfolio.id}
                                  {...register(`portfolios[${portfolio.id}]`)}
                                />
                              </td>
                            )
                          })}
                        </tr>
                      )
                    }
                  })}
                  </tbody>
                  <thead>
                  <tr>
                    <th className="is-selected has-text-centered has-background-grey-special">TOTALS</th>
                    {totals?.length > 0 && totals.map((total, index) => (
                      <th
                        className={`is-selected has-text-centered ${total !== 100 ? 'has-background-danger' : 'has-background-grey-special'}`}
                      >
                        {total}%
                      </th>
                    ))}
                  </tr>
                  </thead>
                </table>
              </div>
            )}

            <div className="columns mb-0">
              <div
                className="column is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start"
              >
                <div className="title is-4 has-text-primary is-flex">Debit Card Creation % Split</div>
                <p className="subtitle is-6">INSTRUCTIONS: Fill in the split % for each debit card processor for each
                  portfolio. Each portfolio&apos;s split % must equal 100%.</p>
              </div>
            </div>

            {responseData.length !== 0 && (
              <>
                <div className="table-container mb-0">
                  <table
                    className="lox-hooktable-table table is-bordered is-striped is-narrow is-fullwidth is-hoverable mb-3"
                  >
                    <thead>
                    <tr>
                      <th className="is-selected has-text-centered"></th>
                      {responseData?.portfolios?.map((portfolio, index) => (
                        <th className="is-selected has-text-centered" key={portfolio.id}>{portfolio.name}</th>
                      ))}
                    </tr>
                    </thead>
                    <tbody>
                    {responseData?.processors?.map((processor) => {
                      if (processor.is_debit_card) {
                        return (
                          <tr key={processor.id}>
                            <td className="has-text-centered">{processor.name}</td>
                            {responseData?.portfolios?.map((portfolio) => {
                              // Find the corresponding split object
                              const split = responseData?.splits?.find(
                                (split) => split.processor_company_dropdown_option_id === processor.id && split.portfolio_id === portfolio.id,
                              )

                              // Extract the split_percentage value or set it to empty string if no split object is found
                              const splitPercentage = split ? split.split_percentage : 0
                              const inputName = `[${portfolio.id}][${processor.id}]`

                              return (
                                <td className="has-text-centered" key={portfolio.id}>
                                  <Controller
                                    name={`split_percentages${inputName}`}
                                    control={control}
                                    defaultValue={splitPercentage}
                                    render={({ field: { onChange } }) => (
                                      <input
                                        {...register(`split_percentages${inputName}`)}
                                        name={`split_percentages${inputName}`}
                                        className="input is-small"
                                        type="number"
                                        form="debit_card_split_form"
                                        min={0}
                                        max={100}
                                        step="1"
                                      />
                                    )}
                                  />
                                  <input
                                    type="hidden"
                                    name={`processors[${processor.id}]`}
                                    value={processor.id}
                                    {...register(`processors[${processor.id}]`)}
                                  />
                                  <input
                                    type="hidden"
                                    name={`portfolios[${portfolio.id}]`}
                                    value={portfolio.id}
                                    {...register(`portfolios[${portfolio.id}]`)}
                                  />
                                </td>
                              )
                            })}
                          </tr>
                        )
                      }
                    })}
                    </tbody>
                    <thead>
                    <tr>
                      <th className="is-selected has-text-centered has-background-grey-special">TOTALS</th>
                      {debitCardTotals?.length > 0 && debitCardTotals.map((total, index) => (
                        <th
                          className={`is-selected has-text-centered ${total !== 100 ? 'has-background-danger' : 'has-background-grey-special'}`}
                        >
                          {total}%
                        </th>
                      ))}
                    </tr>
                    </thead>
                  </table>
                </div>
                <div className="lox-form__buttons-wrapper has-text-right">
                  <button
                    type="button"
                    className="button has-background-grey has-text-white is-small mr-1"
                    onClick={handleCancel}
                  >
                    <span className="mx-3">Cancel</span>
                  </button>
                  <button
                    type="submit"
                    className="button is-link is-small ml-1"
                    disabled={saving && 'disabled'}
                  >
                    {saving ? 'Saving' : 'Save'}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      )}
    />
  )
}
